import React from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { Box, Flex, Icon, Tooltip, Spinner } from 'pounceV2';
import { CorrelationRule, DetectionType } from 'Generated/schema';
import { useGetDetection } from 'Hooks/graphql/getDetection.generated';
import { RuleDetails } from 'Source/graphql/fragments/RuleDetails.generated';
import { useTheme } from '@mui/material';
import { getFlowChartColors, STEP_NODE_MAX_HEIGHT_PX, STEP_NODE_WIDTH_PX } from '../constantsV3';
import { CountInfoV3, NodeIconV3, StepInfoV3, StepTitleV3 } from './Helpers';
import { RuleStepData } from '../utils';

export interface StepNodeData {
  error?: string;
  showHandles?: boolean;
  isLastNode?: boolean;
  stepData: RuleStepData; // data from the YAML
}

const StepNodeV3: React.FC<NodeProps<StepNodeData>> = props => {
  const theme = useTheme();
  const FLOW_CHART_COLORS = getFlowChartColors(theme.palette.tokens);
  const { showHandles = true, isLastNode = false, stepData, error } = props?.data || {};
  const { loading: ruleLoading, data: ruleData } = useGetDetection({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        id: stepData?.RuleID,
      },
    },
    skip: !stepData?.RuleID,
    context: {
      failSilently: true,
    },
  });

  // adds a type to the rule. Policies aren't allowed in Correlation Rules, so those will resolve to null
  let rule;
  switch (ruleData?.detection?.detection?.analysisType) {
    case DetectionType.Rule:
    case DetectionType.ScheduledRule:
      rule = ruleData?.detection?.detection as RuleDetails;
      break;
    case DetectionType.CorrelationRule:
      rule = ruleData?.detection?.detection as CorrelationRule;
      break;
    default:
      rule = null;
  }

  const ruleMissingError =
    ruleData?.detection?.detection?.analysisType === DetectionType.Policy
      ? 'Cannot use policies'
      : 'No rule found';

  return (
    <Box
      background={FLOW_CHART_COLORS.STEP.BACKGROUND}
      border="2px solid"
      borderRadius="xlarge"
      borderColor={rule?.enabled ? FLOW_CHART_COLORS.STEP.BORDER : FLOW_CHART_COLORS.ERROR.BORDER}
      width={`${STEP_NODE_WIDTH_PX}px`}
      maxHeight={`${STEP_NODE_MAX_HEIGHT_PX}px`}
      position="relative"
      p={2}
      sx={{
        backdropFilter: 'blur(12px)',
      }}
      _hover={{
        backgroundColor: FLOW_CHART_COLORS.STEP.BACKGROUND_HOVER,
        borderColor: rule?.enabled
          ? FLOW_CHART_COLORS.STEP.BORDER_HOVER
          : FLOW_CHART_COLORS.ERROR.BORDER,

        cursor: 'default',
      }}
    >
      {showHandles ? (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={false}
          style={{
            opacity: '0',
          }}
        />
      ) : null}

      <Flex direction="row" align="flex-start" gap={2}>
        <>
          <NodeIconV3 analysisType={rule?.analysisType} logTypes={rule?.logTypes} />
          {ruleLoading ? (
            <Flex align="center" justify="center" position="absolute" left={0} right={0}>
              <Spinner size="small" />
            </Flex>
          ) : (
            <Flex direction="column" width="100%" position="relative" gap={1} minWidth={0}>
              <Flex justify="space-between">
                <StepInfoV3
                  list={
                    rule?.analysisType === DetectionType.Rule
                      ? rule?.logTypes
                      : rule?.scheduledQueries
                  }
                  error={error || (!rule ? ruleMissingError : '')}
                />
                {rule && !rule?.enabled ? (
                  <Tooltip
                    content={
                      <Box maxWidth={300}>
                        This rule is currently disabled. Please enable for the Correlation Rule to
                        work.
                      </Box>
                    }
                    placement="right"
                  >
                    <Icon
                      size="medium"
                      type="warning"
                      color={FLOW_CHART_COLORS.ERROR.BORDER}
                      mt={-1}
                    />
                  </Tooltip>
                ) : null}
              </Flex>
              <StepTitleV3 rule={rule} stepRuleID={stepData?.RuleID} />
              <CountInfoV3 stepData={stepData} />
            </Flex>
          )}
        </>
      </Flex>
      {showHandles && !isLastNode ? (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          style={{
            border: `1px solid ${FLOW_CHART_COLORS.TRANSITION.BORDER}`,
            backgroundColor: FLOW_CHART_COLORS.TRANSITION.BORDER,
            width: '8px',
            height: '8px',
          }}
        />
      ) : null}
    </Box>
  );
};

export default StepNodeV3;
